//
// # Add Instance/ Port Mode Handler
//

// Local Imports
import { schdata, randomId } from "GenAlphaModel";
import * as changes from "DrawCore/src/changes";
import { Instance, SchPin } from "DrawCore/src/content/schematic/instance";
import type { Schematic } from "../schematic";
import { ModeHandler } from "../../../modes";
import { Keys } from "../../../keys";
import { RedrawRequest } from "DrawCore/src/redraw";

// Base Class for shared logic between `AddInstance` and `AddSchPin`.
abstract class AddToSchematic extends ModeHandler {
  constructor(readonly schematic: Schematic) {
    super();
  }
  get editor() {
    return this.schematic.editor;
  }

  // Get the Entity being added.
  abstract entity(): Instance | SchPin;
  abstract commit(): unknown;
  // Clear the provisional entity from the schematic (called on abort/commit)
  abstract clearProvisional(): void;

  // On abort, remove the entity and return to `Idle`.
  override abort = () => {
    this.clearProvisional();
    this.editor.content.deselect();
    this.entity().abort();
    this.editor.goUiIdle();
  };

  // Update the location of our in-progress entity.
  updateLoc = (): RedrawRequest => {
    const entity = this.entity();
    const newLoc = this.editor.gridPointNearestToMouse();
    return entity.update({ xform: { ...entity.data.xform, ...newLoc } });
  };

  // Update the location on mouse-move.
  override handleMouseMove = () => this.updateLoc();

  // Commit the instance on mouse-up, i.e. the end of a click to place it.
  override handleMouseUp = () => this.commit();

  // Right-click (any non-left button) cancels; left-click commits via
  // handleMouseUp.
  override handleMouseDown = (e: MouseEvent) => {
    if (e.button !== 0) this.abort();
  };
}

// # Add Instance Mode
// Tracks the pending Instance, which until `commit` time is not added to the schematic.
export class AddInstance extends AddToSchematic {
  private constructor(schematic: Schematic, readonly instance: Instance) {
    super(schematic);
  }

  // Enter this mode
  static enter(schematic: Schematic): unknown {
    return schematic.editor.setMode(AddInstance.start(schematic));
  }

  // Enter this mode at a specific position (used by context menu)
  static enterAt(
    schematic: Schematic,
    position: { x: number; y: number }
  ): unknown {
    return schematic.editor.setMode(AddInstance.start(schematic, position));
  }

  // Create the provisional `Instance`, using the last one added as a template.
  // If `initialPosition` is provided, use it instead of the current mouse position.
  static start(
    schematic: Schematic,
    initialPosition?: { x: number; y: number }
  ): AddInstance {
    //
    // Clone the last instance we added, update its name and position.
    const { lastInstanceData } = schematic;
    const newInstanceData = schdata.Instance.clone(lastInstanceData);
    const prefix = newInstanceData.name[0] || "X";
    newInstanceData.name = `${prefix}${schematic.numInstances}`;

    // Update its location - use provided position or mouse position
    const position =
      initialPosition ?? schematic.editor.gridPointNearestToMouse();
    newInstanceData.xform = schdata.Xform.fromParts(
      position,
      newInstanceData.xform
    );

    // FIXME: this maybe could be done somewhere easier, or just kept in the "drawn instances"? TBD.
    // But this particularly needs to evaluate the new instance-name, or it'll draw the old one.
    schdata.Instance.evaluateExprs(newInstanceData);

    // Create the provisional `Instance`. Note it is *not* added to the schematic yet.
    const instance = new Instance({
      data: newInstanceData,
      parent: schematic.render,
    });

    // Render-only provisional; not in the committed instances set until commit.
    schematic.provisionalInstances.add(instance);

    // Update our UI state.
    schematic.select(instance);

    // And return our mode handler.
    return new AddInstance(schematic, instance);
  }

  // Clear the provisional instance from the schematic
  override clearProvisional = (): void => {
    this.schematic.provisionalInstances.delete(this.instance);
  };

  // Handle a keystroke, potentially producing a change of kind.
  override handleKey = (e: KeyboardEvent) => {
    switch (e.key) {
      case Keys.Escape:
        return this.abort();
      case Keys.Enter:
        return this.commit();
      default:
        return; // No-op
    }
  };

  // Add the currently-pending entity to the schematic.
  override commit() {
    const { editor, schematic } = this;

    // Clear provisional before adding to committed set
    this.clearProvisional();

    // FIXME: update dots incrementally, instead of re-inferring them all!
    schematic.updateDots();
    const entity = this.entity();

    // Add it to to the schematic.
    schematic.addEntity(entity, false);

    // Notify the changeLog and platform of the change.
    schematic.logChange(changes.Add.new(entity));

    // NOTE: leaving it as the selected entity. That behavior could go either way? TBD.
    // editor.deselect();
    editor.goUiIdle();
  }

  // Our entity is our Instance
  override entity = (): Instance => this.instance;
}

// Set up the keyboard shortcuts for changing `PortDir`s.
const keyboardShortcuts = new Map<string, schdata.PortDir>();
keyboardShortcuts.set("i", schdata.PortDir.Input);
keyboardShortcuts.set("o", schdata.PortDir.Output);
keyboardShortcuts.set("x", schdata.PortDir.Inout);

// # Add Schematic Pin Mode
export class AddSchPin extends AddToSchematic {
  private constructor(schematic: Schematic, readonly pin: SchPin) {
    super(schematic);
  }

  // Enter this mode
  static enter(schematic: Schematic): unknown {
    return schematic.editor.setMode(AddSchPin.start(schematic));
  }

  // Enter this mode at a specific position (used by context menu)
  static enterAt(
    schematic: Schematic,
    position: { x: number; y: number }
  ): unknown {
    return schematic.editor.setMode(AddSchPin.start(schematic, position));
  }

  // Enter this mode with a pre-configured pin spec (used by AddPinDialog).
  // The user can then move the pin around before committing.
  static enterWithSpec(
    schematic: Schematic,
    spec: {
      name: string;
      direction: schdata.PortDir;
      position: { x: number; y: number };
    }
  ): unknown {
    return schematic.editor.setMode(AddSchPin.startWithSpec(schematic, spec));
  }

  // Create a new `SchPin` and start moving it around.
  // If `initialPosition` is provided, use it instead of the current mouse position.
  static start(
    schematic: Schematic,
    initialPosition?: { x: number; y: number }
  ): AddSchPin {
    // Create the provisional `SchPin`, using the last one added as a template.
    const { lastSchPinSpec } = schematic;
    const pinSpec = schdata.SchPinSpec.clone(lastSchPinSpec);

    // Assign a unique ID for change tracking and server sync
    pinSpec.id = randomId();

    // Update position if provided
    if (initialPosition) {
      pinSpec.xform = schdata.Xform.fromParts(initialPosition, pinSpec.xform);
    }

    const pin = schematic.createPin(pinSpec);

    // Render-only provisional; not in the committed pins set until commit.
    schematic.provisionalPins.add(pin);

    // Update our UI state.
    schematic.select(pin);

    // And return our mode handler.
    return new AddSchPin(schematic, pin);
  }

  // Create a new `SchPin` with a pre-configured spec from the AddPinDialog.
  static startWithSpec(
    schematic: Schematic,
    spec: {
      name: string;
      direction: schdata.PortDir;
      position: { x: number; y: number };
    }
  ): AddSchPin {
    // Create the pin spec with the provided parameters
    const pinSpec = schdata.SchPinSpec.new(
      spec.name,
      spec.direction,
      spec.position
    );

    // Assign a unique ID for change tracking and server sync
    pinSpec.id = randomId();

    // Store as lastSchPinSpec so future pins use these settings
    schematic.lastSchPinSpec = schdata.SchPinSpec.clone(pinSpec);

    const pin = schematic.createPin(pinSpec);

    // Render-only provisional; not in the committed pins set until commit.
    schematic.provisionalPins.add(pin);

    // Update our UI state.
    schematic.select(pin);

    // And return our mode handler.
    return new AddSchPin(schematic, pin);
  }

  // Clear the provisional pin from the schematic
  override clearProvisional = (): void => {
    this.schematic.provisionalPins.delete(this.pin);
  };

  // Handle a keystroke, potentially producing a change of kind.
  override handleKey = (e: KeyboardEvent) => {
    if (e.key === Keys.Escape) {
      return this.abort();
    }
    if (e.key === Keys.Enter) {
      return this.commit();
    }
    const newKind = keyboardShortcuts.get(e.key);
    if (newKind) {
      // We hit a shortcut key and have a valid new type
      return this.changePortDir(newKind);
    }
  };

  // Change the `PortDir` of the in-progress `SchPin`. Also refreshes
  // `labelFmt` to the new direction's default so the text label moves to
  // the appropriate side — same fix as `Schematic.updatePinDirectionById`.
  changePortDir = (dir: schdata.PortDir) => {
    const { schematic, pin } = this;
    const newSymbol = schdata.SchPinSymbol.get(dir);
    schematic.lastSchPinSpec.direction = dir;
    schematic.lastSchPinSpec.labelFmt = newSymbol.defaultLabelFmt;
    pin.update({ direction: dir, labelFmt: newSymbol.defaultLabelFmt });
  };

  // Add the currently-pending `SchPin` to the schematic.
  override commit() {
    // Couple things to do here:
    // - FIXME: test for a valid port name
    // - Add the `SchPin`, and potentially a corresponding `SchPort`, to the schematic
    // - Update the drawing object to a "committed" version instead of "provisional"
    //
    const { editor, schematic, pin } = this;

    // Clear provisional before adding to committed set
    this.clearProvisional();

    // Add it to to the schematic.
    schematic.addEntity(pin, false);

    // Notify the changeLog and platform of the change.
    schematic.logChange(changes.add(pin));

    // FIXME: update dots incrementally, instead of re-inferring them all!
    schematic.updateDots();

    // NOTE: leaving it as the selected entity. That behavior could go either way? TBD.
    // editor.deselect();
    editor.goUiIdle();
  }

  // Our entity is our Port
  entity = () => this.pin;
}
